import { DownOutlined, FilePdfOutlined, MailOutlined, MinusCircleOutlined, PlusOutlined, WhatsAppOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert, App, Button, Card, Col, DatePicker, Descriptions, Dropdown, Flex, Form, Input, InputNumber, Modal, Result, Row, Select, Skeleton, Space, Table, Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { ApiError, api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { useActiveWarehouse, useWarehouses } from "@/lib/useWarehouses";
import { SkuSelect } from "../catalog/mapping";
import { PoStatusTag, SupplierSelect, rupiahInput, taxPercent, type PurchaseOrder } from "./common";

type Line = NonNullable<PurchaseOrder["lines"]>[number];
type Receipt = NonNullable<PurchaseOrder["receipts"]>[number];
type FormLine = { skuId?: string; quantity?: number; unitPrice?: number | null; note?: string };
type FormValues = {
  supplierId?: string;
  warehouseId?: string;
  orderDate?: Dayjs;
  expectedDate?: Dayjs | null;
  paymentTermsDays?: number | null;
  taxRateBp?: number;
  note?: string;
  lines: FormLine[];
};

const DAY = "YYYY-MM-DD";
const TAX_CHOICES = [0, 1100, 1200];

/**
 * A purchase order (Plan §6.18): written as a draft here, submitted to the
 * owner, approved or sent back, then sent to the supplier and received.
 * /app/procurement/orders/new starts a draft (?supplierId preselects one).
 */
export function PurchaseOrderDetail() {
  const { poID = "" } = useParams();
  const isNew = poID === "new";
  const { t } = useI18n();
  const { can } = useAuth();
  const errorText = useErrorText();
  const po = useQuery({
    queryKey: ["purchase-orders", "detail", poID],
    queryFn: () => unwrap(api.GET("/v1/purchase-orders/{poID}", { params: { path: { poID } } })),
    enabled: !isNew,
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  useTabTitle(isNew ? t("po.add") : po.data?.number);

  if (isNew) return can("procurement.manage") ? <Editor /> : <Result status="403" title={t("page.forbidden.title")} />;
  if (po.isError) {
    return po.error instanceof ApiError && po.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(po.error)} />;
  }
  if (!po.data) return <Skeleton active />;
  if (po.data.status === "draft" && can("procurement.manage")) return <Editor po={po.data} />;
  return <View po={po.data} />;
}

function useRefresh() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["purchase-orders"] });
    void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    void queryClient.invalidateQueries({ queryKey: ["suppliers"] });
  };
}

function Editor({ po }: { po?: PurchaseOrder }) {
  const { t, formatMoney } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const [params] = useSearchParams();
  const warehouses = useWarehouses();
  const { current } = useActiveWarehouse();
  const [form] = Form.useForm<FormValues>();
  const supplierId = Form.useWatch("supplierId", form);
  const lines = Form.useWatch("lines", form) ?? [];
  const taxRateBp = Form.useWatch("taxRateBp", form) ?? 0;

  // The supplier's prices fill a line's price when its SKU is chosen.
  const supplier = useQuery({
    queryKey: ["suppliers", "detail", supplierId],
    queryFn: () => unwrap(api.GET("/v1/suppliers/{supplierID}", { params: { path: { supplierID: supplierId! } } })),
    enabled: !!supplierId,
  });
  const priceOf = (skuId: string) => {
    const p = supplier.data?.prices.find((x) => x.skuId === skuId);
    return p?.contractPrice ?? p?.lastPrice ?? null;
  };

  const initial: FormValues = po
    ? {
        supplierId: po.supplierId,
        warehouseId: po.warehouseId,
        orderDate: dayjs(po.orderDate, DAY),
        expectedDate: po.expectedDate ? dayjs(po.expectedDate, DAY) : null,
        paymentTermsDays: po.paymentTermsDays,
        taxRateBp: po.taxRateBp,
        note: po.note,
        lines: (po.lines ?? []).map((l) => ({ skuId: l.skuId, quantity: l.quantity, unitPrice: l.unitPrice, note: l.note })),
      }
    : { supplierId: params.get("supplierId") ?? undefined, warehouseId: current?.id, orderDate: dayjs(), taxRateBp: 0, lines: [{}] };

  const subtotal = lines.reduce((sum, l) => sum + (l?.quantity ?? 0) * (l?.unitPrice ?? 0), 0);
  const tax = Math.floor((subtotal * taxRateBp + 5000) / 10000);

  const save = useMutation({
    mutationFn: async ({ v, submit }: { v: FormValues; submit: boolean }) => {
      const body = {
        supplierId: v.supplierId!,
        warehouseId: v.warehouseId!,
        orderDate: (v.orderDate ?? dayjs()).format(DAY),
        expectedDate: v.expectedDate ? v.expectedDate.format(DAY) : null,
        paymentTermsDays: v.paymentTermsDays ?? null,
        taxRateBp: v.taxRateBp ?? 0,
        note: v.note?.trim() ?? "",
        lines: v.lines
          .filter((l) => l.skuId && l.quantity)
          .map((l) => ({ skuId: l.skuId!, quantity: l.quantity!, unitPrice: l.unitPrice ?? null, note: l.note?.trim() ?? "" })),
      };
      const saved = po
        ? await unwrap(api.PUT("/v1/purchase-orders/{poID}", { params: { path: { poID: po.id } }, body }))
        : await unwrap(api.POST("/v1/purchase-orders", { body }));
      if (submit) return unwrap(api.POST("/v1/purchase-orders/{poID}/submit", { params: { path: { poID: saved.id } } }));
      return saved;
    },
    onSuccess: (saved, { submit }) => {
      void message.success(submit ? t("po.submitted") : t("po.draftSaved"));
      refresh();
      if (!po) navigate(`/app/procurement/orders/${saved.id}`, { replace: true });
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });

  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      initialValues={initial}
      onValuesChange={(changed: Partial<FormValues>) => {
        // A new supplier brings its PPN and payment terms.
        if (changed.supplierId) {
          void (async () => {
            const s = await unwrap(api.GET("/v1/suppliers/{supplierID}", { params: { path: { supplierID: changed.supplierId! } } }));
            form.setFieldsValue({ taxRateBp: s.pkp ? 1100 : 0, paymentTermsDays: s.paymentTermsDays });
          })();
        }
      }}
    >
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {po?.number ?? t("po.add")}
          </Typography.Title>
          {po && <PoStatusTag status={po.status} />}
        </Space>
        <Space wrap>
          {po && <PdfButton po={po} />}
          {po && <CancelButton po={po} />}
          <Button loading={save.isPending && !save.variables?.submit} onClick={() => form.validateFields().then((v) => save.mutate({ v, submit: false }))}>
            {t("po.saveDraft")}
          </Button>
          <Button type="primary" loading={save.isPending && !!save.variables?.submit} onClick={() => form.validateFields().then((v) => save.mutate({ v, submit: true }))}>
            {t("po.submit")}
          </Button>
        </Space>
      </Flex>
      {po?.rejectNote && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("po.rejected", { note: po.rejectNote })} />}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="supplierId" label={t("po.supplier")} rules={[{ required: true, message: t("field.required") }]}>
              <SupplierSelect />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="warehouseId" label={t("po.warehouse")} rules={[{ required: true, message: t("field.required") }]}>
              <Select options={warehouses.accessible.map((w) => ({ value: w.id, label: w.name }))} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="orderDate" label={t("po.orderDate")}>
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="expectedDate" label={t("po.expectedDate")}>
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="paymentTermsDays" label={t("po.paymentTerms")}>
              <InputNumber min={0} max={365} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={12} md={6}>
            <Form.Item name="taxRateBp" label={t("po.tax")}>
              <Select options={TAX_CHOICES.map((bp) => ({ value: bp, label: bp ? t("po.taxRate", { rate: taxPercent(bp) }) : t("po.taxNone") }))} />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Card size="small" title={t("po.lines")} style={{ marginBottom: 16 }}>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map((f) => {
                const l = lines[f.name] ?? {};
                return (
                  <Flex key={f.key} gap={8} align="start" wrap>
                    <Form.Item name={[f.name, "skuId"]} style={{ flex: "1 1 280px" }} rules={[{ required: true, message: t("field.required") }]}>
                      <SkuSelect
                        singleOnly
                        onChange={(id) => {
                          const price = priceOf(id);
                          const current = form.getFieldValue(["lines", f.name, "unitPrice"]);
                          if (price != null && current == null) form.setFieldValue(["lines", f.name, "unitPrice"], price);
                        }}
                      />
                    </Form.Item>
                    <Form.Item name={[f.name, "quantity"]} rules={[{ required: true, message: t("field.required") }]}>
                      <InputNumber min={1} max={1_000_000} precision={0} placeholder={t("po.quantity")} style={{ width: 110 }} />
                    </Form.Item>
                    <Form.Item name={[f.name, "unitPrice"]} tooltip={t("po.unitPriceHelp")}>
                      <InputNumber<number> min={0} max={1_000_000_000_000} precision={0} prefix="Rp" placeholder={t("po.unitPrice")} style={{ width: 170 }} {...rupiahInput} />
                    </Form.Item>
                    <Typography.Text className="z-num" style={{ width: 140, textAlign: "right", paddingTop: 5 }}>
                      {formatMoney((l.quantity ?? 0) * (l.unitPrice ?? 0))}
                    </Typography.Text>
                    {fields.length > 1 && <Button type="text" icon={<MinusCircleOutlined />} aria-label={t("common.delete")} onClick={() => remove(f.name)} />}
                  </Flex>
                );
              })}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})} block>
                {t("po.addLine")}
              </Button>
            </>
          )}
        </Form.List>
        <Totals subtotal={subtotal} tax={tax} taxRateBp={taxRateBp} />
      </Card>
      <Form.Item name="note" label={t("po.note")}>
        <Input.TextArea rows={3} maxLength={1000} />
      </Form.Item>
    </Form>
  );
}

function Totals({ subtotal, tax, taxRateBp }: { subtotal: number; tax: number; taxRateBp: number }) {
  const { t, formatMoney } = useI18n();
  return (
    <Flex vertical align="end" gap={4} style={{ marginTop: 16 }}>
      <Flex gap={24}>
        <Typography.Text type="secondary">{t("po.subtotal")}</Typography.Text>
        <Typography.Text className="z-num" style={{ minWidth: 140, textAlign: "right" }}>
          {formatMoney(subtotal)}
        </Typography.Text>
      </Flex>
      {taxRateBp > 0 && (
        <Flex gap={24}>
          <Typography.Text type="secondary">{t("po.taxRate", { rate: taxPercent(taxRateBp) })}</Typography.Text>
          <Typography.Text className="z-num" style={{ minWidth: 140, textAlign: "right" }}>
            {formatMoney(tax)}
          </Typography.Text>
        </Flex>
      )}
      <Flex gap={24}>
        <Typography.Text strong>{t("po.total")}</Typography.Text>
        <Typography.Text strong className="z-num" style={{ minWidth: 140, textAlign: "right" }}>
          {formatMoney(subtotal + tax)}
        </Typography.Text>
      </Flex>
    </Flex>
  );
}

function PdfButton({ po }: { po: PurchaseOrder }) {
  const { message } = App.useApp();
  const errorText = useErrorText();
  const open = async () => {
    const win = window.open("", "_blank");
    try {
      const blob = await unwrap(api.GET("/v1/purchase-orders/{poID}/pdf", { params: { path: { poID: po.id } }, parseAs: "blob" }));
      const url = URL.createObjectURL(blob);
      if (win && !win.closed) win.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      win?.close();
      void message.error(errorText(err));
    }
  };
  return (
    <Button icon={<FilePdfOutlined />} onClick={() => void open()}>
      PDF
    </Button>
  );
}

function CancelButton({ po }: { po: PurchaseOrder }) {
  const { t } = useI18n();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const [reason, setReason] = useState("");
  if (!["draft", "submitted", "approved", "sent"].includes(po.status)) return null;
  return (
    <Button
      danger
      onClick={() =>
        modal.confirm({
          title: t("po.cancelConfirm", { number: po.number }),
          content: <Input.TextArea rows={2} maxLength={500} placeholder={t("po.cancelReason")} onChange={(e) => setReason(e.target.value)} />,
          okText: t("po.cancel"),
          okButtonProps: { danger: true },
          cancelText: t("common.close"),
          onOk: async () => {
            try {
              await unwrap(api.POST("/v1/purchase-orders/{poID}/cancel", { params: { path: { poID: po.id } }, body: { reason } }));
              refresh();
            } catch (err) {
              void message.error(errorText(err));
            }
          },
        })
      }
    >
      {t("po.cancel")}
    </Button>
  );
}

function View({ po }: { po: PurchaseOrder }) {
  const { t, formatMoney, formatNumber, formatDate, formatDateTime } = useI18n();
  const { can, me } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const tz = me?.workspace?.timezone;
  const [rejecting, setRejecting] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const manage = can("procurement.manage");
  const approve = can("procurement.approve");
  const live = ["approved", "sent", "partial"].includes(po.status);
  const path = { params: { path: { poID: po.id } } };

  const act = useMutation<unknown, Error, "approve" | "close" | "markSent" | "share" | "receipt">({
    mutationFn: (kind) => {
      switch (kind) {
        case "approve":
          return unwrap(api.POST("/v1/purchase-orders/{poID}/approve", path));
        case "close":
          return unwrap(api.POST("/v1/purchase-orders/{poID}/close", path));
        case "markSent":
          return unwrap(api.POST("/v1/purchase-orders/{poID}/mark-sent", path));
        case "share":
          return unwrap(api.POST("/v1/purchase-orders/{poID}/share", path));
        case "receipt":
          return unwrap(api.POST("/v1/purchase-orders/{poID}/receipts", path));
      }
    },
    onSuccess: (res, kind) => {
      refresh();
      const out = res as { whatsappUrl?: string; receiptId?: string };
      if (kind === "approve") void message.success(t("po.approved"));
      if (kind === "share" && out.whatsappUrl) {
        window.open(out.whatsappUrl, "_blank");
        void message.success(t("po.whatsappOpened"));
      }
      if (kind === "receipt" && out.receiptId) navigate(`/app/warehouse/inbound/${out.receiptId}`);
    },
    onError: (err) => void message.error(errorText(err)),
  });

  const sentLabel = po.sentAt ? t("po.sentOn", { when: formatDateTime(po.sentAt, tz), via: po.sentVia ? t(`po.sentVia.${po.sentVia}`) : "" }) : null;

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} className="z-num" style={{ margin: 0, fontSize: 28 }}>
            {po.number}
          </Typography.Title>
          <PoStatusTag status={po.status} />
          <Typography.Link onClick={() => navigate(`/app/procurement/suppliers/${po.supplierId}`)}>{po.supplierName}</Typography.Link>
        </Space>
        <Space wrap>
          <PdfButton po={po} />
          {po.status === "submitted" && approve && (
            <>
              <Button onClick={() => setRejecting(true)}>{t("po.reject")}</Button>
              <Button type="primary" loading={act.isPending && act.variables === "approve"} onClick={() => act.mutate("approve")}>
                {t("po.approve")}
              </Button>
            </>
          )}
          {live && manage && (
            <Dropdown
              menu={{
                items: [
                  { key: "email", icon: <MailOutlined />, label: t("po.sendEmail"), onClick: () => setEmailing(true) },
                  { key: "wa", icon: <WhatsAppOutlined />, label: t("po.sendWhatsApp"), onClick: () => act.mutate("share") },
                  { key: "mark", label: t("po.markSent"), onClick: () => act.mutate("markSent") },
                ],
              }}
            >
              <Button>
                {t("po.send")} <DownOutlined />
              </Button>
            </Dropdown>
          )}
          {live && (can("procurement.manage") || can("warehouse.manage")) && (
            <Button
              type="primary"
              loading={act.isPending && act.variables === "receipt"}
              onClick={() => (po.warehouseWms ? act.mutate("receipt") : setReceiving(true))}
            >
              {po.warehouseWms ? t("po.openReceipt") : t("po.receive")}
            </Button>
          )}
          {live && manage && (
            <Button
              onClick={() =>
                modal.confirm({
                  title: t("po.close"),
                  content: t("po.closeConfirm", { number: po.number }),
                  okText: t("po.close"),
                  cancelText: t("common.cancel"),
                  onOk: () => act.mutateAsync("close"),
                })
              }
            >
              {t("po.close")}
            </Button>
          )}
          {manage && <CancelButton po={po} />}
        </Space>
      </Flex>
      {po.status === "submitted" && !approve && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("po.waitingApproval")} />}
      {live && po.warehouseWms && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("po.openReceiptHelp", { warehouse: po.warehouseName })} />}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={8}>
          <Card size="small">
            <Descriptions
              column={1}
              size="small"
              items={[
                { key: "wh", label: t("po.warehouse"), children: po.warehouseName },
                { key: "date", label: t("po.orderDate"), children: formatDate(po.orderDate) },
                { key: "exp", label: t("po.expectedDate"), children: po.expectedDate ? formatDate(po.expectedDate) : "—" },
                { key: "terms", label: t("po.paymentTerms"), children: po.paymentTermsDays === 0 ? t("po.cod") : t("po.paymentTermsValue", { n: po.paymentTermsDays }) },
                { key: "tax", label: t("po.tax"), children: po.taxRateBp ? t("po.taxRate", { rate: taxPercent(po.taxRateBp) }) : t("po.taxNone") },
                { key: "by", label: t("po.createdBy"), children: `${po.createdBy} · ${formatDateTime(po.createdAt, tz)}` },
                { key: "ok", label: t("po.approvedBy"), children: po.approvedAt ? `${po.approvedBy} · ${formatDateTime(po.approvedAt, tz)}` : "—" },
                ...(sentLabel ? [{ key: "sent", label: t("po.status.sent"), children: sentLabel }] : []),
                ...(po.note ? [{ key: "note", label: t("po.note"), children: <span style={{ whiteSpace: "pre-wrap" }}>{po.note}</span> }] : []),
                ...(po.cancelReason ? [{ key: "cancel", label: t("po.cancelReason"), children: po.cancelReason }] : []),
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={16}>
          <Card size="small" title={t("po.lines")}>
            <Table<Line>
              size="small"
              rowKey="id"
              dataSource={po.lines ?? []}
              pagination={false}
              scroll={{ x: 640 }}
              columns={[
                {
                  title: t("catalog.code"),
                  render: (_, l) => (
                    <span>
                      <span className="z-num">{l.skuCode}</span>
                      <br />
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {l.skuName}
                      </Typography.Text>
                    </span>
                  ),
                },
                { title: t("po.quantity"), dataIndex: "quantity", width: 80, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
                { title: t("po.unitPrice"), dataIndex: "unitPrice", width: 130, align: "right", render: (v: number) => <span className="z-num">{formatMoney(v)}</span> },
                { title: t("po.amount"), dataIndex: "amount", width: 140, align: "right", render: (v: number) => <span className="z-num">{formatMoney(v)}</span> },
                { title: t("po.received"), dataIndex: "received", width: 90, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
                {
                  title: t("po.outstanding"),
                  dataIndex: "outstanding",
                  width: 80,
                  align: "right",
                  render: (v: number) => (
                    <Typography.Text className="z-num" type={v > 0 && !["closed", "cancelled"].includes(po.status) ? "warning" : "secondary"}>
                      {formatNumber(v)}
                    </Typography.Text>
                  ),
                },
              ]}
            />
            <Totals subtotal={po.totals.subtotal} tax={po.totals.tax} taxRateBp={po.taxRateBp} />
          </Card>
          {(po.receipts ?? []).length > 0 && (
            <Card size="small" title={t("po.receipts")} style={{ marginTop: 16 }}>
              <Table<Receipt>
                size="small"
                rowKey="id"
                dataSource={po.receipts}
                pagination={false}
                onRow={(r) => (po.warehouseWms && can("warehouse.operate") ? { onClick: () => navigate(`/app/warehouse/inbound/${r.id}`), style: { cursor: "pointer" } } : {})}
                columns={[
                  { title: t("wms.in.number"), dataIndex: "number", width: 140, render: (v: string) => <span className="z-num">{v}</span> },
                  { title: t("common.status"), dataIndex: "status", width: 130, render: (v: Receipt["status"]) => t(`wms.in.status.${v}`) },
                  { title: t("po.received"), dataIndex: "received", width: 100, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
                  { title: t("po.createdBy"), render: (_, r) => `${r.createdBy} · ${formatDateTime(r.completedAt ?? r.createdAt, tz)}` },
                ]}
              />
            </Card>
          )}
        </Col>
      </Row>
      <RejectModal po={po} open={rejecting} onClose={() => setRejecting(false)} />
      <EmailModal po={po} open={emailing} onClose={() => setEmailing(false)} />
      <ReceiveModal po={po} open={receiving} onClose={() => setReceiving(false)} />
    </>
  );
}

function RejectModal({ po, open, onClose }: { po: PurchaseOrder; open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const [note, setNote] = useState("");
  const reject = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/purchase-orders/{poID}/reject", { params: { path: { poID: po.id } }, body: { note: note.trim() } })),
    onSuccess: () => {
      refresh();
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal open={open} title={t("po.rejectTitle")} okText={t("po.reject")} cancelText={t("common.cancel")} okButtonProps={{ disabled: !note.trim() }} confirmLoading={reject.isPending} onOk={() => reject.mutate()} onCancel={onClose} destroyOnHidden>
      <Input.TextArea rows={3} maxLength={500} placeholder={t("po.rejectNote")} value={note} onChange={(e) => setNote(e.target.value)} />
    </Modal>
  );
}

function EmailModal({ po, open, onClose }: { po: PurchaseOrder; open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const refresh = useRefresh();
  const [form] = Form.useForm<{ to?: string; message?: string }>();
  const send = useMutation({
    mutationFn: (v: { to?: string; message?: string }) =>
      unwrap(api.POST("/v1/purchase-orders/{poID}/email", { params: { path: { poID: po.id } }, body: { to: v.to?.trim() ?? "", message: v.message?.trim() ?? "" } })),
    onSuccess: () => {
      void message.success(t("po.emailed"));
      refresh();
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal open={open} title={t("po.emailTitle")} okText={t("po.sendEmail")} cancelText={t("common.cancel")} confirmLoading={send.isPending} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => send.mutate(v)}>
        <Form.Item name="to" label={t("po.emailTo")} extra={t("po.emailToHelp")} rules={[{ type: "email", message: t("field.invalid") }]}>
          <Input placeholder={po.supplierEmail || ""} inputMode="email" />
        </Form.Item>
        <Form.Item name="message" label={t("po.emailMessage")}>
          <Input.TextArea rows={3} maxLength={1000} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function ReceiveModal({ po, open, onClose }: { po: PurchaseOrder; open: boolean; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const refresh = useRefresh();
  const outstanding = useMemo(() => (po.lines ?? []).filter((l) => l.outstanding > 0), [po.lines]);
  const [qty, setQty] = useState<Record<string, number | null>>({});
  const value = (l: Line) => (l.skuId in qty ? qty[l.skuId] : l.outstanding);
  const receive = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/purchase-orders/{poID}/receive", {
          params: { path: { poID: po.id } },
          body: { lines: outstanding.map((l) => ({ skuId: l.skuId, quantity: value(l) ?? 0 })).filter((l) => l.quantity > 0) },
        }),
      ),
    onSuccess: () => {
      void message.success(t("po.receivedOk"));
      refresh();
      setQty({});
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const total = outstanding.reduce((s, l) => s + (value(l) ?? 0), 0);
  return (
    <Modal open={open} width={680} title={t("po.receiveTitle")} okText={t("po.receive")} cancelText={t("common.cancel")} okButtonProps={{ disabled: total === 0 }} confirmLoading={receive.isPending} onOk={() => receive.mutate()} onCancel={onClose} destroyOnHidden>
      <Typography.Paragraph type="secondary">{t("po.receiveHelp", { warehouse: po.warehouseName })}</Typography.Paragraph>
      <Table<Line>
        size="small"
        rowKey="id"
        dataSource={outstanding}
        pagination={false}
        columns={[
          { title: t("catalog.code"), render: (_, l) => <span className="z-num">{l.skuCode}</span> },
          { title: t("common.name"), dataIndex: "skuName" },
          { title: t("po.outstanding"), dataIndex: "outstanding", width: 90, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
          {
            title: t("po.received"),
            width: 120,
            render: (_, l) => <InputNumber min={0} max={1_000_000} precision={0} value={value(l)} onChange={(v) => setQty((x) => ({ ...x, [l.skuId]: v }))} style={{ width: "100%" }} />,
          },
        ]}
      />
    </Modal>
  );
}
